import { Component } from "react";
import { AlertTriangle, RotateCcw, RefreshCw } from "lucide-react";
import { reportClientError, isChunkError, maybeReloadForChunk } from "@/lib/report";

export default class ErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", this.props.name || "app", error, info?.componentStack);
    // Stale code-split chunk after a redeploy → recover seamlessly with a
    // one-time reload instead of showing the error card.
    if (isChunkError(error) && maybeReloadForChunk()) return;
    reportClientError({
      kind: "react",
      name: error?.name,
      message: error?.message || String(error),
      stack: error?.stack,
      componentStack: info?.componentStack,
      section: this.props.name,
    });
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div data-testid="error-boundary-card" className="k-glass rounded-2xl p-8 max-w-xl mx-auto my-10 text-center space-y-4">
        <div className="mx-auto w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center">
          <AlertTriangle size={22} className="text-rose-600" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">Something went wrong here</h3>
          <p className="text-sm text-gray-500 mt-1">
            This section hit an unexpected error. The rest of Kairos is still running — you can retry this section or reload the app.
          </p>
        </div>
        <div className="flex items-center justify-center gap-2">
          <button data-testid="error-boundary-retry" onClick={() => this.setState({ error: null })}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-lg brand-grad text-white hover:opacity-90 transition-opacity">
            <RotateCcw size={14} /> Retry
          </button>
          <button data-testid="error-boundary-reload" onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors">
            <RefreshCw size={14} /> Reload app
          </button>
        </div>
        <details className="text-left">
          <summary className="text-[11px] text-gray-400 cursor-pointer hover:text-gray-600">Technical details</summary>
          <pre className="mt-2 text-[10px] text-rose-700 bg-rose-50/60 rounded-lg p-3 overflow-auto max-h-40 whitespace-pre-wrap">
            {String(this.state.error?.stack || this.state.error)}
          </pre>
        </details>
      </div>
    );
  }
}
